import { TZDateMini } from '@date-fns/tz';
import { createSelector } from '@reduxjs/toolkit';
import { isSameDay } from 'date-fns';
import { isEmpty, pluck } from 'ramda';
import {
  selectCartForLineItems,
  selectCartWithPrices,
  selectMaxPromoFreeQtySubmitWarning,
  selectShowMaxPromoFreeItemCountWarning,
} from '../cart/selectors';
import ooeConstants from '../constants';
import { selectEventDetails, selectLeadTimeWarning } from '../eventDetails/selectors';
import {
  generateTimeSlots,
  generateTimeSlotsForToday,
  isDateBeforeRestaurantToday,
} from '../eventDetails/utils';
import {
  selectMaxDeliveryAmount,
  selectMaxPickupAmount,
  selectMaxSameDayOrderTotalAmountCents,
  selectMinDeliveryAmount,
  selectMinPickupAmount,
} from '../floatingMenu/selectors';
import {
  selectCmtCustomerSubmitWarning,
  selectCmtCustomerCFAId,
  selectGuestValues,
  selectIsCmtCustomerSelected,
  selectPayment,
} from '../guest/selectors';
import { selectIndexedMenuItems, selectMenuPaperGoods } from '../menu/selectors';
import {
  selectDeliveryAddressChanged,
  selectDeliveryAddressSubmitWarning,
  selectDeliveryRangeWarning,
  selectDeliveryTip,
  selectDeliveryTipAmount,
  selectDestination,
  selectDestinationValid,
  selectDetailsDate,
  selectDetailsTime,
  selectEditMode,
  selectEditOrderOriginalCustomerDetails,
  selectHasOrderUpdateErrors,
  selectNewPaymentMethod,
  selectOrderForDXE,
  selectOrderIsLoading,
  selectSmsConsent,
  selectSubTotalAmount,
  selectTaxAmount,
  selectValidAddressError,
  selectSecondaryContactSubmitWarning,
  selectSecondaryContactValid,
  selectSecondaryContactValues,
} from '../order/selectors';
import {
  selectAllFormsValid,
  selectFormSubmitWarning,
  selectIsSmsConsentMissingForDelivery,
  selectPaymentSubmitWarning,
  selectPayWithVaultedCard,
  selectSmsConsentWarning,
} from '../payment/selectors';
import {
  selectBypassBusinessRules,
  selectLocationAcceptsDelivery,
  selectLocationAcceptsPickup,
  selectLocationNumber,
  selectLocationTimezone,
} from '../user/selectors';
import { submitWarningErrorMessages } from '../util/customerErrorMessages';
import { formatPrice } from '../util/format';
import { roundNumber } from '../util/utils';
import { isCustomerAccount } from '../guest/utils';
import { getVariation } from '../services/launchDarkly';

export const selectOrderForAPI = createSelector(
  selectOrderForDXE,
  selectEventDetails,
  selectLocationNumber,
  selectCartForLineItems,
  selectBypassBusinessRules,
  selectSecondaryContactValues,
  selectDeliveryTip,
  selectSmsConsent,
  selectMenuPaperGoods,
  (
    orderDetails,
    eventDetails,
    locationNumber,
    lineItems,
    bypassBusinessRules,
    secondaryContact,
    deliveryTip,
    smsConsent,
    menuPaperGoods,
  ) => {
    const lineItemsToUse = lineItems.map((lineItem) => {
      let { modifiers } = lineItem;
      const { comboItems } = lineItem;
      if (lineItem.comboItems && lineItem.comboItems?.length !== 0) {
        modifiers.forEach((mod) => {
          comboItems?.forEach((comboItem, i) => {
            if (mod.comboTag === comboItem.itemTag) {
              const newMods = comboItem.modifiers || [];
              if (!newMods.find((aMod) => aMod.itemTag)) {
                newMods.push(mod);
              }
              comboItems[i].modifiers = newMods;
            }
          });
        });
        modifiers = [];
      }
      return {
        ...lineItem,
        modifiers,
        comboItems,
      };
    });

    const isNotEmptyValues = Object.values(secondaryContact).some(
      (x) => x !== null && x !== '' && typeof x === 'string',
    );

    const isObjectWithKeys = (
      value: unknown,
    ): value is { firstName: unknown; lastName: unknown; phoneNumber: unknown } =>
      value != null &&
      typeof value === 'object' &&
      'firstName' in value &&
      'lastName' in value &&
      'phoneNumber' in value;

    const secondaryContactIsValid = isObjectWithKeys(secondaryContact);

    let orderToSend: typeof orderDetails &
      typeof eventDetails & {
        locationNumber: typeof locationNumber;
        lineItems?: typeof lineItemsToUse;
        deliveryTip: typeof deliveryTip;
        bypassBusinessRulesList?: string[];
        secondaryContact?: typeof secondaryContact;
        sendCustomerNotifications?: boolean;
      } = {
      ...orderDetails,
      ...eventDetails,
      locationNumber,
      lineItems: lineItemsToUse,
      deliveryTip,
      sendCustomerNotifications: smsConsent ?? false,
    };
    if (orderToSend.lineItems?.length === 0) {
      orderToSend = { ...orderToSend, status: 'Cart', lineItems: undefined };
    }
    if (bypassBusinessRules) {
      orderToSend = {
        ...orderToSend,
        bypassBusinessRulesList: ['all'],
      };
    }
    if (secondaryContactIsValid && isNotEmptyValues) {
      orderToSend = {
        ...orderToSend,
        secondaryContact,
      };
    }

    if (getVariation('showPaperGoods', false)) {
      const paperGoodsTags = menuPaperGoods?.items.map((item) => item.tag);
      if (paperGoodsTags && !isEmpty(paperGoodsTags)) {
        orderToSend = {
          ...orderToSend,
          paperGoods: !!orderToSend.lineItems?.some((item) => paperGoodsTags.includes(item.itemTag)),
        };
      }
    }
    return orderToSend;
  },
);

export const selectOrderWithPayment = createSelector(
  selectOrderForAPI,
  selectPayWithVaultedCard,
  selectNewPaymentMethod,
  selectPayment,
  (orderState, payWithVaultedCard, newPaymentMethod, payment) => {
    if (payWithVaultedCard) {
      return { ...orderState, payment, status: 'Submit' };
    }
    if (!newPaymentMethod) {
      return {
        ...orderState,
        payment: { paymentType: 'TO_BE_COLLECTED' },
        status: 'Submit',
      };
    }
    return { ...orderState, status: 'Cart' };
  },
);

export const selectDateIsToday = createSelector(
  selectEditMode,
  selectDetailsDate,
  selectLocationTimezone,
  (editMode, date, timeZone) =>
    // detailsDate carries no zone, so its calendar fields are re-read in the restaurant's zone
    !!date &&
    !editMode &&
    isSameDay(
      new TZDateMini(date.getFullYear(), date.getMonth(), date.getDate(), timeZone),
      TZDateMini.tz(timeZone),
    ),
);

// Plain selector: wall-clock "today" must recompute on every read (AC6 midnight).
export const selectPromiseDateHasPassed = (
  state: Parameters<typeof selectEditMode>[0] & Parameters<typeof selectLocationTimezone>[0],
) => {
  const editMode = selectEditMode(state);
  const date = selectDetailsDate(state);
  const timeZone = selectLocationTimezone(state);
  return !!editMode && !!date && isDateBeforeRestaurantToday(date, timeZone);
};

export const selectPromiseDateSubmitWarning = createSelector(selectPromiseDateHasPassed, (hasPassed) =>
  hasPassed
    ? {
        type: 'error',
        message: submitWarningErrorMessages.PROMISE_DATE_PASSED,
      }
    : {},
);

export const selectExceedsSameDayMaxOrderTotal = createSelector(
  selectSubTotalAmount,
  selectMaxSameDayOrderTotalAmountCents,
  selectDateIsToday,
  (subtotal, maxSameDayOrderTotalAmount, dateIsToday) => {
    return (
      dateIsToday &&
      typeof maxSameDayOrderTotalAmount === 'number' &&
      subtotal * 100 > maxSameDayOrderTotalAmount
    );
  },
);

export const selectTimeShouldClear = createSelector(
  selectDetailsTime,
  selectDateIsToday,
  selectBypassBusinessRules,
  selectLocationTimezone,
  (time, dateIsToday, bypassBusinessRules, timeZone) => {
    const timeSlots = dateIsToday ? generateTimeSlotsForToday(timeZone) : generateTimeSlots();
    const availableTimeSlots = timeSlots.filter((timeSlot) => timeSlot.available);
    const availableTimes = pluck('time', availableTimeSlots);
    const selectedTimeNotAvailable = availableTimes.indexOf(time) === -1;
    return bypassBusinessRules && selectedTimeNotAvailable;
  },
);

export const selectDeliveryMinNotMet = createSelector(
  selectDestination,
  selectSubTotalAmount,
  selectMinDeliveryAmount,
  (destination, subtotal, minDeliveryAmount) => {
    return (
      destination === ooeConstants.DELIVERY &&
      typeof minDeliveryAmount === 'number' &&
      subtotal * 100 <= minDeliveryAmount
    );
  },
);

export const selectPickupMinNotMet = createSelector(
  selectDestination,
  selectSubTotalAmount,
  selectMinPickupAmount,
  (destination, subtotal, minPickupAmount) =>
    destination === ooeConstants.PICKUP &&
    typeof minPickupAmount === 'number' &&
    subtotal * 100 <= minPickupAmount,
);

export const selectMaxOrderAmount = createSelector(
  selectDestination,
  selectMaxDeliveryAmount,
  selectMaxPickupAmount,
  (destination, deliveryMax, pickupMax) => {
    let maxOrderAmount;
    if (destination === ooeConstants.DELIVERY) {
      maxOrderAmount = deliveryMax;
    } else if (destination === ooeConstants.PICKUP) {
      maxOrderAmount = pickupMax;
    }
    return maxOrderAmount;
  },
);

export const selectExceedsMaxOrderTotal = createSelector(
  selectSubTotalAmount,
  selectMaxOrderAmount,
  (subtotal, maxOrderTotalAmount) =>
    typeof maxOrderTotalAmount === 'number' && subtotal * 100 > maxOrderTotalAmount,
);

export const selectSubmitButtonIsDisabled = createSelector(
  selectDeliveryMinNotMet,
  selectPickupMinNotMet,
  selectExceedsMaxOrderTotal,
  selectAllFormsValid,
  selectBypassBusinessRules,
  selectOrderIsLoading,
  selectDestinationValid,
  selectHasOrderUpdateErrors,
  selectValidAddressError,
  selectDeliveryRangeWarning,
  selectDeliveryAddressChanged,
  selectExceedsSameDayMaxOrderTotal,
  selectSecondaryContactValid,
  selectShowMaxPromoFreeItemCountWarning,
  selectIsSmsConsentMissingForDelivery,
  selectPromiseDateHasPassed,
  (
    deliveryMinNotMet,
    pickupMinNotMet,
    exceedsMaxOrderTotal,
    allFormsValid,
    bypassBusinessRules,
    orderIsLoading,
    addressValid,
    hasOrderUpdateErrors,
    validAddressError,
    deliveryRangeWarning,
    deliveryAddressChanged,
    exceedsMaxSameDay,
    secondaryContactValid,
    showMaxPromoFreeItemCountWarning,
    smsConsentMissingForDelivery,
    promiseDateHasPassed,
  ) => {
    const disabledByBusinessRules =
      deliveryMinNotMet ||
      pickupMinNotMet ||
      exceedsMaxOrderTotal ||
      exceedsMaxSameDay ||
      !!deliveryRangeWarning;

    const disabledRegardlessOfBusinessRules =
      hasOrderUpdateErrors ||
      !allFormsValid ||
      orderIsLoading ||
      (!!validAddressError && deliveryAddressChanged) ||
      !secondaryContactValid ||
      showMaxPromoFreeItemCountWarning ||
      smsConsentMissingForDelivery ||
      promiseDateHasPassed;

    if (bypassBusinessRules) {
      // CMT-941 - the fact that !addressValid is only checked
      // when bypassing business rules is probably a bug
      return disabledRegardlessOfBusinessRules || !addressValid;
    }
    return disabledRegardlessOfBusinessRules || disabledByBusinessRules;
  },
);

export const selectSubtotalSubmitWarning = createSelector(
  selectDeliveryMinNotMet,
  selectMinDeliveryAmount,
  selectPickupMinNotMet,
  selectMinPickupAmount,
  selectExceedsMaxOrderTotal,
  selectMaxOrderAmount,
  selectExceedsSameDayMaxOrderTotal,
  selectMaxSameDayOrderTotalAmountCents,
  (
    deliveryMinNotMet,
    minDeliveryAmount,
    pickupMinNotMet,
    minPickupAmount,
    exceedsMax,
    maxAmount,
    exceedsMaxSameDay,
    maxSameDayAmount,
  ) => {
    if (exceedsMax) {
      return {
        type: 'warning',
        message: `${submitWarningErrorMessages.ORDER_MAX}${maxAmount && formatPrice(maxAmount / 100)}.`,
      };
    }
    if (exceedsMaxSameDay) {
      return {
        type: 'warning',
        message: `${submitWarningErrorMessages.SAME_DAY_ORDER_MAX}${maxSameDayAmount && formatPrice(maxSameDayAmount / 100)}.`,
      };
    }
    if (deliveryMinNotMet) {
      return {
        type: 'warning',
        message: `${submitWarningErrorMessages.DELIVERY_MIN}${minDeliveryAmount && formatPrice(minDeliveryAmount / 100)}.`,
      };
    }
    if (pickupMinNotMet) {
      return {
        type: 'warning',
        message: `${submitWarningErrorMessages.PICKUP_MIN}${minPickupAmount && formatPrice(minPickupAmount / 100)}.`,
      };
    }
    return {};
  },
);

export const selectAllSubmitWarnings = createSelector(
  selectSubtotalSubmitWarning,
  selectFormSubmitWarning,
  selectDeliveryAddressSubmitWarning,
  selectPaymentSubmitWarning,
  selectCmtCustomerSubmitWarning,
  selectLeadTimeWarning,
  selectSecondaryContactSubmitWarning,
  selectMaxPromoFreeQtySubmitWarning,
  selectSmsConsentWarning,
  selectPromiseDateSubmitWarning,
  (
    subtotalWarnings,
    formWarnings,
    destinationWarning,
    paymentWarning,
    cfaGuestWarning,
    leadTime,
    secondaryContact,
    promoFreeMaxQty,
    smsConsentWarning,
    promiseDateWarning,
  ) =>
    [
      subtotalWarnings,
      formWarnings,
      destinationWarning,
      paymentWarning,
      cfaGuestWarning,
      leadTime,
      secondaryContact,
      promoFreeMaxQty,
      smsConsentWarning,
      promiseDateWarning,
    ].filter((warning) => !isEmpty(warning)),
);

export const selectTaxAndTotal = createSelector(
  selectTaxAmount,
  selectSubTotalAmount,
  selectDeliveryTipAmount,
  (tax = 0, subtotal = 0, tipAmount = 0) => formatPrice(tax + subtotal + tipAmount),
);

export const selectDestinationNotValidForLocation = createSelector(
  selectDestination,
  selectLocationAcceptsDelivery,
  selectLocationAcceptsPickup,
  (destination, acceptsDelivery, acceptsPickup) =>
    (destination === ooeConstants.DELIVERY && !acceptsDelivery) ||
    (destination === ooeConstants.PICKUP && !acceptsPickup),
);

export const selectPromoFreeItemsValue = createSelector(
  selectCartWithPrices,
  selectIndexedMenuItems,
  (cartWithPrices, menuItems) => {
    const promoFreeItemsValue = cartWithPrices
      .filter((x) => x.promoFree)
      .reduce(
        (accumulator, current) => accumulator + (menuItems[current.tag]?.itemPrice * current.quantity || 0),
        0,
      );
    return roundNumber(promoFreeItemsValue);
  },
);

export const selectGuestNeedsUpdate = createSelector(
  selectGuestValues,
  selectEditOrderOriginalCustomerDetails,
  selectIsCmtCustomerSelected,
  (guest, editOrderGuest, cfaSelected) =>
    !!editOrderGuest &&
    editOrderGuest.email === guest.email &&
    !isCustomerAccount({ accountType: editOrderGuest.accountType }) &&
    !cfaSelected,
);

export const selectNeedsProfilePatchCall = createSelector(
  selectGuestValues,
  selectEditOrderOriginalCustomerDetails,
  selectIsCmtCustomerSelected,
  (guest, editOrderGuest, cfaSelected) =>
    !cfaSelected &&
    (!editOrderGuest ||
      editOrderGuest.givenName !== guest.first ||
      editOrderGuest.familyName !== guest.last ||
      editOrderGuest.phone !== guest.phone ||
      editOrderGuest.email !== guest.email ||
      isCustomerAccount({ accountType: editOrderGuest.accountType })),
);
export const selectEditNeedsCancellation = createSelector(
  selectCmtCustomerCFAId,
  selectEditOrderOriginalCustomerDetails,
  selectGuestValues,
  (cfaId, editOrderGuest, guestFormValues) =>
    (cfaId && cfaId !== editOrderGuest?.cfaId) ||
    (!cfaId &&
      (editOrderGuest?.email !== guestFormValues.email ||
        isCustomerAccount({ accountType: editOrderGuest?.accountType }))),
);
